import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useDashboard from '../hooks/useDashboard';
import Logger from '../utils/logger-adapter';
import LogoImage from '../assets/logo.jpg';
import MetamaskLogo from '../assets/metamask-logo.png';
import UniswapLogo from '../assets/uniswap-logo.svg';
import { Link } from 'react-router-dom';
import './Dashboard.css';

// Format number with appropriate decimal places - MAINTENU INTACT
const formatNumber = (value, decimals = 2) => {
  if (!value && value !== 0) return '0';
  
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  
  // Format for very small numbers
  if (Math.abs(num) < 0.000001 && num !== 0) {
    return num.toExponential(4);
  }
  
  // Format for large numbers
  if (Math.abs(num) >= 1000000) {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }
  
  // Format for medium numbers
  if (Math.abs(num) >= 1) {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }
  
  // Format for small numbers
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: 6
  });
};

// Format percentage values - MAINTENU INTACT
const formatPercent = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0%';
  
  if (num < 0.001 && num > 0) return '<0.001%';
  return num.toFixed(2) + '%';
};

// Calculate contract data for display - MAINTENU INTACT, CRITIQUE POUR LES METRICS
const useContractDataFormatted = (contractState) => {
  return useMemo(() => {
    // Default values for safety
    const totalSupply = contractState?.totalSupply || '0';
    const totalBurned = contractState?.totalBurned || '0';
    const circulatingSupply = parseFloat(totalSupply) - parseFloat(contractState?.contractBalance || '0');
    const contractBalance = contractState?.contractBalance || '0';
    const maxBurnSupply = contractState?.maxBurnSupply || '0';
    const rewardsPool = contractState?.rewardsPool || '0';
    const totalStaked = contractState?.totalStaked || '0';
    const totalLPStaked = contractState?.totalLPStaked || '0';
    
    // CORRECTION CRITIQUE: Récupération correcte des données Uniswap
    const ethReserve = contractState?.uniswapReserves?.ethReserve || '0';
    const gr33dReserve = contractState?.uniswapReserves?.gr33dReserve || '0';
    const ethPrice = contractState?.uniswapReserves?.ethPrice || '0';
    const totalLiquidity = contractState?.uniswapReserves?.totalLiquidity || '0';
    
    // Calcul du prix GR33D (s'il n'est pas directement disponible)
    const gr33dPrice = parseFloat(gr33dReserve) > 0 && parseFloat(ethReserve) > 0 && parseFloat(ethPrice) > 0
      ? (parseFloat(ethReserve) * parseFloat(ethPrice)) / parseFloat(gr33dReserve)
      : '0';
    
    const maxTxLimit = '15000'; // Valeur par défaut basée sur la constante du contrat
    
    // Calculated fields
    const burnRemaining = Math.max(0, parseFloat(maxBurnSupply) - parseFloat(totalBurned));
    const circulatingPercent = parseFloat(totalSupply) > 0 
      ? (circulatingSupply / parseFloat(totalSupply)) * 100 
      : 0;
    
    return {
      totalSupply,
      totalBurned,
      circulatingSupply,
      contractBalance,
      maxBurnSupply,
      rewardsPool,
      totalStaked,
      totalLPStaked,
      ethReserve,
      gr33dReserve,
      gr33dPrice,
      ethPrice,
      totalLiquidity,
      maxTxLimit,
      burnRemaining,
      circulatingPercent
    };
  }, [contractState]);
};

// Progress bar component with animation
const ProgressBar = ({ current, max, label = '', color = 'primary', height = 10, showLabels = true, showValue = true }) => {
  const percentage = Math.min(Math.max((parseFloat(current) / parseFloat(max)) * 100, 0), 100);
  
  // Determine gradient based on color
  const getGradient = () => {
    switch(color) {
      case 'success':
        return 'linear-gradient(90deg, #4c76af, #8bc4ea)';
      case 'warning':
        return 'linear-gradient(90deg, #ff9f1c, #ffc107)';
      case 'danger':
        return 'linear-gradient(90deg, #f44336, #ff5722)';
      case 'burn':
        return 'linear-gradient(90deg, #ff4444, #ff7744)';
      case 'staking':
        return 'linear-gradient(90deg, #4c76af, #8bc4ea)';
      case 'liquidity':
        return 'linear-gradient(90deg, #5e4caf, #8c4caf)';
      default:
        return 'linear-gradient(90deg, #4c76af, #7c56af)';
    }
  };
  
  return (
    <div className="dashboard-progress-wrapper">
      {label && (
        <div className="dashboard-progress-header">
          <span className="dashboard-progress-label text-secondary">{label}</span>
          {showValue && <span className="dashboard-progress-value primary-color">{formatNumber(current)} / {formatNumber(max)}</span>}
        </div>
      )}
      
      <div className="dashboard-progress-container" style={{ height: `${height}px` }}>
        <motion.div 
          className="dashboard-progress-bar"
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          style={{
            background: getGradient(),
            height: '100%'
          }}
        />
      </div>
      
      {showLabels && (
        <div className="dashboard-progress-footer">
          <span className="dashboard-progress-min text-secondary">0</span>
          <span className="dashboard-progress-percent primary-color">{percentage.toFixed(2)}%</span>
          <span className="dashboard-progress-max text-secondary">{formatNumber(max)}</span>
        </div>
      )}
    </div>
  );
};

// Metric Component for displaying individual stats with unified design
const MetricTile = ({ label, value, unit = "", color = "#4c76af", tooltip = "", highlight = false, size = "medium" }) => {
  return (
    <motion.div 
      className={`dashboard-metric-tile feature-card ${highlight ? 'dashboard-highlight' : ''} dashboard-size-${size}`}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
    >
      <div className="dashboard-metric-content">
        <div className="dashboard-metric-label text-secondary">{label}</div>
        <div className="dashboard-metric-value" style={{ color }}>
          {value}
          {unit && <span className="dashboard-metric-unit text-secondary">{unit}</span>}
        </div>
        {tooltip && <div className="dashboard-metric-tooltip text-secondary">{tooltip}</div>}
      </div>
    </motion.div>
  );
};

// Badge Component for status indicators with unified styling
const StatusBadge = ({ status, text }) => {
  // Determine badge colors
  const getStatusClass = () => {
    switch(status) {
      case 'success':
      case 'active':
      case 'enabled':
      case 'locked':
        return 'dashboard-status-success';
      case 'warning':
      case 'pending':
      case 'unlocked':
        return 'dashboard-status-warning';
      case 'error':
      case 'inactive':
      case 'disabled':
        return 'dashboard-status-error';
      default:
        return '';
    }
  };
  
  return (
    <span className={`dashboard-status-badge ${getStatusClass()}`}>
      {text}
    </span>
  );
};

// Main Dashboard component
const Dashboard = () => {
  const { isLoading, error, lastUpdateTime, contractState, refreshData } = useDashboard();
  const contractData = useContractDataFormatted(contractState);
  
  const [hasCopied, setHasCopied] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };
  
  // Handle copying contract address
  const copyContractAddress = useCallback(() => {
    const contractAddress = "0xC3b2990027217b9970b2d526aa11Ba3f223eb39C";
    
    navigator.clipboard.writeText(contractAddress)
      .then(() => {
        setHasCopied(true);
        setTimeout(() => setHasCopied(false), 2000);
        Logger.log('interaction', 'Contract address copied');
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        // Fallback method for browsers without clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = contractAddress;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setHasCopied(true);
        setTimeout(() => setHasCopied(false), 2000);
      });
  }, []);
  
  // Handle refresh data with success notification
  const handleRefresh = useCallback(() => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    setRefreshSuccess(false);
    Logger.log('interaction', 'Manual refresh triggered');
    
    try {
      if (refreshData && typeof refreshData === 'function') {
        refreshData();
        // Montrer la notification de succès après un court délai
        setTimeout(() => {
          setRefreshSuccess(true);
          // Masquer après 3 secondes
          setTimeout(() => setRefreshSuccess(false), 3000);
        }, 1000);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
    
    setTimeout(() => setIsRefreshing(false), 1000);
  }, [isRefreshing, refreshData]);
  
  // Handle adding token to MetaMask (compatible with mobile)
  const addToMetamask = useCallback(() => {
    if (!window.ethereum) {
      alert('MetaMask is not installed!');
      return;
    }
    
    try {
      // Configuration uniforme pour tous les navigateurs/devices
      window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: '0xC3b2990027217b9970b2d526aa11Ba3f223eb39C',
            symbol: 'GR33D',
            decimals: 18,
            image: 'https://gr33d-vault.vercel.app/logo192.png',
          },
        },
      })
      .then((success) => {
        if (success) {
          console.log('GR33D token added to MetaMask');
          Logger.log('interaction', 'Token added to MetaMask successfully');
        } else {
          console.log('User rejected adding GR33D token to MetaMask');
        }
      })
      .catch((error) => {
        console.error('Error adding token to MetaMask:', error);
      });
    } catch (error) {
      console.error('Error adding token to MetaMask:', error);
    }
  }, []);
  
  // Calculate a readable time for lastUpdateTime
  const getLastUpdateTimeDisplay = useCallback(() => {
    if (!lastUpdateTime) return 'Never updated';
    
    return lastUpdateTime.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }, [lastUpdateTime]);
  
  return (
    <div className="dashboard-section">
      {/* NIVEAU 1: Conteneur principal englobant */}
      <div className="gr33d-container">
        {/* Loading overlay - only for initial loading */}
        <AnimatePresence>
          {isLoading && !lastUpdateTime && (
            <motion.div 
              className="dashboard-loading-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="dashboard-loading-spinner"></div>
              <p className="text-primary">Loading GR33D data...</p>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Notification de succès de rafraîchissement */}
        <AnimatePresence>
          {refreshSuccess && (
            <motion.div 
              className="dashboard-refresh-success"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <span className="dashboard-success-icon">✓</span>
              <span className="text-primary">Data refreshed successfully!</span>
            </motion.div>
          )}
        </AnimatePresence>
        
        <motion.div
          className="dashboard-wrapper"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Dashboard Header - NIVEAU 2 */}
          <motion.section 
            className="dashboard-header gr33d-element animate-on-scroll"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="dashboard-header-content">
              <div className="dashboard-logo-title-container">
                <motion.img 
                  src={LogoImage} 
                  alt="GR33D Logo" 
                  className="dashboard-logo golden-pulse-effect"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                />
                <div className="dashboard-title-container">
                  <h1 className="gr33d-title glowing-text">GR33D Ecosystem Dashboard</h1>
                  <p className="dashboard-description text-secondary">Real-time metrics and analytics for the GR33D token</p>
                </div>
              </div>
              <div className="dashboard-header-actions">
                <button 
                  className="dashboard-refresh-button g-button g-button-primary"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  type="button"
                >
                  {isRefreshing ? (
                    <>
                      <span className="dashboard-refresh-spinner"></span>
                      <span>Refreshing...</span>
                    </>
                  ) : (
                    <>
                      <span className="dashboard-refresh-icon">↻</span>
                      <span>Refresh Data</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.section>
          
          {/* Error message */}
          <AnimatePresence>
            {error && (
              <motion.div 
                className="dashboard-error-container"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="dashboard-error-message">
                  <span className="dashboard-error-icon">⚠️</span>
                  <span className="text-primary">{error}</span>
                  <button 
                    className="dashboard-error-retry"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    Retry
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Dashboard Content - Grid uniforme de 2 colonnes */}
          <div className="dashboard-grid">
            {/* Price & metrics section (maintenant de taille standard) */}
            <motion.section 
              className="gr33d-element animate-on-scroll"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
                  <div className="text-center">
                <h2 className="dashboard-card-title highlight-blue">
                  <span>💰</span>
                  Price & Metrics
                </h2>
              </div>
              
              <div className="section-spacing"></div>
              
              <motion.div 
                className="dashboard-price-card feature-card"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="dashboard-price-value gold-value golden-text">
                  ${formatNumber(contractData.gr33dPrice, 6)}
                </div>
                <div className="dashboard-price-label text-secondary">$GR33D PRICE</div>
              </motion.div>
              
              <div className="dashboard-metrics-grid dashboard-metrics-grid-3col">
                <MetricTile 
                  label="Market Cap" 
                  value={`$${formatNumber(parseFloat(contractData.gr33dPrice) * parseFloat(contractData.circulatingSupply))}`} 
                  color="#7c56af"
                />
                <MetricTile 
                  label="Total Liquidity" 
                  value={`$${formatNumber(contractData.totalLiquidity)}`} 
                  color="#4c76af"
                />
                <MetricTile 
                  label="ETH Price" 
                  value={`$${formatNumber(contractData.ethPrice)}`} 
                  color="#627eea"
                />
              </div>
              
              <div className="dashboard-action-buttons">
                <motion.a
                  href="https://app.uniswap.org/swap?outputCurrency=0xC3b2990027217b9970b2d526aa11Ba3f223eb39C"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dashboard-uniswap-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={UniswapLogo} alt="Uniswap" className="dashboard-uniswap-logo" />
                  Trade on Uniswap
                </motion.a>
                
                <motion.a
                  href="/staking"
                  className="dashboard-staking-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Stake $GR33D
                </motion.a>
              </div>
            </motion.section>

            {/* Contract Information Card */}
            <motion.section 
              className="gr33d-element animate-on-scroll"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center">
                <h2 className="dashboard-card-title highlight-blue">
                  <span>📄</span>
                  Contract Information
                </h2>
              </div>
              
              <div className="section-spacing"></div>
              
              <div className="dashboard-contract-address-container feature-card">
                <div className="dashboard-address-wrapper">
                  <div className="dashboard-address-label text-secondary">Contract Address:</div>
                  <div className="dashboard-address-value primary-color">0xC3b2990027217b9970b2d526aa11Ba3f223eb39C</div>
                </div>
                <motion.button 
                  className={`dashboard-copy-button ${hasCopied ? 'dashboard-copied' : ''}`}
                  onClick={copyContractAddress}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {hasCopied ? "Copied!" : "Copy"}
                </motion.button>
              </div>
              
              <div className="dashboard-contract-details">
                <div className="dashboard-detail-row">
                  <div className="dashboard-detail-item feature-card">
                    <span className="dashboard-detail-label text-secondary">Name</span>
                    <span className="dashboard-detail-value text-primary">{contractState.name || "GR33DVAULT"}</span>
                  </div>
                  <div className="dashboard-detail-item feature-card">
                    <span className="dashboard-detail-label text-secondary">Symbol</span>
                    <span className="dashboard-detail-value text-primary">{contractState.symbol || "GR33D"}</span>
                  </div>
                </div>
                <div className="dashboard-detail-row">
                  <div className="dashboard-detail-item feature-card">
                    <span className="dashboard-detail-label text-secondary">Trading Status</span>
                    <StatusBadge 
                      status={contractState.tradingEnabled ? 'enabled' : 'disabled'} 
                      text={contractState.tradingEnabled ? 'Enabled' : 'Disabled'} 
                    />
                  </div>
                  <div className="dashboard-detail-item feature-card">
                    <span className="dashboard-detail-label text-secondary">Liquidity Status</span>
                    <StatusBadge 
                      status="locked" 
                      text="Locked" 
                    />
                  </div>
                </div>
              </div>
              
              <div className="dashboard-metamask-wrapper">
                <motion.button 
                  className="dashboard-metamask-button"
                  onClick={addToMetamask}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={MetamaskLogo} alt="MetaMask" className="dashboard-metamask-icon" />
                  <span>Add to MetaMask</span>
                </motion.button>
              </div>
            </motion.section>

            {/* Token Supply Card */}
            <motion.section 
              className="gr33d-element animate-on-scroll"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center">
                <h2 className="dashboard-card-title highlight-blue">
                  <span>💰</span>
                  Token Supply
                </h2>
              </div>
              
              <div className="section-spacing"></div>
              
              <div className="dashboard-metrics-grid dashboard-metrics-grid-2col">
                <MetricTile 
                  label="Total Supply" 
                  value={formatNumber(contractData.totalSupply)} 
                  unit="GR33D"
                  color="#4c76af"
                  size="large"
                />
                <MetricTile 
                  label="Circulating Supply" 
                  value={formatNumber(contractData.circulatingSupply)} 
                  unit="GR33D"
                  tooltip={`${formatPercent(contractData.circulatingPercent)} of total supply`}
                  color="#7c56af"
                  size="large"
                />
              </div>
              
              <div className="dashboard-progress-section">
                <ProgressBar 
                  current={contractData.circulatingSupply} 
                  max={contractData.totalSupply} 
                  label="Circulating Supply" 
                  color="liquidity"
                  height={12}
                />
              </div>
              
              <div className="dashboard-metrics-grid dashboard-metrics-grid-2col">
                <MetricTile 
                  label="Contract Balance" 
                  value={formatNumber(contractData.contractBalance)} 
                  unit="GR33D"
                  color="#4c76af"
                />
                <MetricTile 
                  label="Transaction Limit" 
                  value={formatNumber(contractData.maxTxLimit)} 
                  unit="GR33D"
                  tooltip="Standard transaction limit"
                  color="#ffd700"
                />
              </div>
            </motion.section>

            {/* Burn Mechanism Card */}
            <motion.section 
              className="gr33d-element animate-on-scroll"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center">
                <h2 className="dashboard-card-title highlight-blue">
                  <span>🔥</span>
                  Burn Mechanism
                </h2>
              </div>
              
              <div className="section-spacing"></div>
              
              <div className="dashboard-metrics-grid dashboard-metrics-grid-2col">
                <MetricTile 
                  label="Total Burned" 
                  value={formatNumber(contractData.totalBurned)} 
                  unit="GR33D"
                  color="#ff4444"
                  highlight={true}
                  size="large"
                />
                <MetricTile 
                  label="Max Burn Supply" 
                  value={formatNumber(contractData.maxBurnSupply)} 
                  unit="GR33D"
                  color="#ff9f1c"
                  size="large"
                />
              </div>
              
              <div className="dashboard-progress-section">
                <ProgressBar 
                  current={contractData.totalBurned} 
                  max={contractData.maxBurnSupply} 
                  label="Burn Progress" 
                  color="burn"
                  height={12}
                />
              </div>
              
              <div className="dashboard-metrics-grid dashboard-metrics-grid-3col">
                <MetricTile 
                  label="Burn Rate (Standard)" 
                  value="0.5" 
                  unit="%"
                  color="#ff4444"
                />
                <MetricTile 
                  label="Burn Rate (Staking)" 
                  value="0.25" 
                  unit="%"
                  color="#ff9f1c"
                />
                <MetricTile 
                  label="Remaining Burnable" 
                  value={formatNumber(contractData.burnRemaining)} 
                  unit="GR33D"
                  color="#4c76af"
                />
              </div>
            </motion.section>

            {/* Staking Metrics Card */}
            <motion.section 
              className="gr33d-element animate-on-scroll"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center">
                <h2 className="dashboard-card-title highlight-blue">
                  <span>🔒</span>
                  Staking Metrics
                </h2>
              </div>
              
              <div className="section-spacing"></div>
              
              <div className="dashboard-metrics-grid dashboard-metrics-grid-2col">
                <MetricTile 
                  label="Total Staked" 
                  value={formatNumber(contractData.totalStaked)} 
                  unit="GR33D"
                  color="#4caf50"
                  highlight={true}
                  size="large"
                />
                <MetricTile 
                  label="Rewards Pool" 
                  value={formatNumber(contractData.rewardsPool)} 
                  unit="GR33D"
                  color="#ffd700"
                  size="large"
                />
              </div>
              
              <div className="dashboard-progress-section">
                <ProgressBar 
                  current={contractData.totalStaked} 
                  max={contractData.totalSupply} 
                  label="Staking Progress" 
                  color="staking"
                  height={12}
                />
              </div>
              
              <div className="dashboard-metrics-grid dashboard-metrics-grid-3col">
                <MetricTile 
                  label="LP Staked" 
                  value={formatNumber(contractData.totalLPStaked)} 
                  unit="LP Tokens"
                  color="#4c76af"
                />
                <MetricTile 
                  label="Base APY" 
                  value="20" 
                  unit="%"
                  color="#4caf50"
                />
                <MetricTile 
                  label="LP APY" 
                  value="80" 
                  unit="%"
                  color="#ffd700"
                />
              </div>
            </motion.section>

            {/* Uniswap Liquidity Card */}
            <motion.section 
              className="gr33d-element animate-on-scroll"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-center">
                <h2 className="dashboard-card-title highlight-blue">
                  <span>💧</span>
                  Uniswap Liquidity
                </h2>
              </div>
              
              <div className="section-spacing"></div>
              
              <div className="dashboard-metrics-grid dashboard-metrics-grid-2col">
                <MetricTile 
                  label="GR33D Reserve" 
                  value={formatNumber(contractData.gr33dReserve)} 
                  unit="GR33D"
                  color="#7c56af"
                  size="large"
                />
                <MetricTile 
                  label="ETH Reserve" 
                  value={formatNumber(contractData.ethReserve, 6)} 
                  unit="ETH"
                  tooltip={`~$${formatNumber(parseFloat(contractData.ethReserve) * parseFloat(contractData.ethPrice))}`}
                  color="#627eea"
                  size="large"
                />
              </div>
              
              <div className="dashboard-pool-info feature-card">
                <div className="dashboard-pool-address">
                  <h4 className="highlight-blue">Uniswap V2 Pair</h4>
                  <span className="dashboard-pool-address-value primary-color">0x8a1D8f57261e8832CE1D7C525Df76dbe002B2e25</span>
                </div>
              </div>
              
              <div className="dashboard-uniswap-section">
                <motion.a
                  href="https://app.uniswap.org/positions/v2/ethereum/0x8a1D8f57261e8832CE1D7C525Df76dbe002B2e25"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dashboard-uniswap-button dashboard-pool-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={UniswapLogo} alt="Uniswap" className="dashboard-uniswap-logo" />
                  View Pool on Uniswap
                </motion.a>
              </div>
            </motion.section>
            
            {/* Ecosystem Links Card - en pleine largeur */}
            <motion.section 
              className="gr33d-element animate-on-scroll ecosystem-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ gridColumn: "1 / -1" }}
            >
              <div className="text-center">
                <h2 className="dashboard-card-title highlight-blue">
                  <span>🔗</span>
                  Ecosystem Links
                </h2>
              </div>
              
              <div className="section-spacing"></div>
              
              <div className="dashboard-ecosystem-grid gr33d-grid gr33d-grid-4">
                <Link to="/art" className="dashboard-ecosystem-link feature-card">
                  <div className="dashboard-ecosystem-icon">🎨</div>
                  <div className="dashboard-ecosystem-text">
                    <h3 className="highlight-blue">Art Marketplace</h3>
                    <p className="text-secondary">Explore the upcoming marketplace for digital and physical art</p>
                  </div>
                </Link>
                
                <Link to="/whitepaper" className="dashboard-ecosystem-link feature-card">
                  <div className="dashboard-ecosystem-icon">📄</div>
                  <div className="dashboard-ecosystem-text">
                    <h3 className="highlight-blue">Whitepaper</h3>
                    <p className="text-secondary">Read the technical details of the GR33D ecosystem</p>
                  </div>
                </Link>
                
                <Link to="/roadmap" className="dashboard-ecosystem-link feature-card">
                  <div className="dashboard-ecosystem-icon">🗺️</div>
                  <div className="dashboard-ecosystem-text">
                    <h3 className="highlight-blue">Roadmap</h3>
                    <p className="text-secondary">Discover our development timeline and future plans</p>
                  </div>
                </Link>
                
                <Link to="/contacts" className="dashboard-ecosystem-link feature-card">
                  <div className="dashboard-ecosystem-icon">👥</div>
                  <div className="dashboard-ecosystem-text">
                    <h3 className="highlight-blue">Community</h3>
                    <p className="text-secondary">Join our community channels and follow our progress</p>
                  </div>
                </Link>
              </div>
            </motion.section>
          </div>
          
          {/* Dashboard Footer */}
          <motion.footer 
            className="dashboard-footer gr33d-element"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="dashboard-footer-content">
              <div className="dashboard-update-info">
                <span className="dashboard-update-label text-secondary">Last updated:</span>
                <span className="dashboard-update-time primary-color">{getLastUpdateTimeDisplay()}</span>
                <span className="dashboard-update-frequency text-secondary">(Auto-refreshes hourly, use Refresh button for latest data)</span>
              </div>
              <div className="dashboard-footer-logo">
                <span className="dashboard-footer-logo-text primary-color">GR33D</span>
                <span className="dashboard-footer-version text-secondary">v2.0.0</span>
              </div>
            </div>
          </motion.footer>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
